import {
    addMinutes,
    endOfHour,
    getHours,
    getMinutes,
    isPast,
    roundToNearestMinutes,
    set,
    startOfToday,
} from "date-fns";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverContent } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

type LocalDateTimePickerProps = {
    onChange?: (newDate: Date | undefined) => void;
    onConfirm: (date: Date) => void;
    onCancel: () => void;
    showTime?: boolean | null | undefined;
    initialDate?: Date;
};

const minuteStep = 15;
const hours = Array.from({ length: 24 }, (_, hour) => hour);
const stepMinutes = Array.from({ length: 60 / minuteStep }, (_, i) => i * minuteStep);

function pad(value: number): string {
    return String(value).padStart(2, "0");
}

function toItems(values: number[]) {
    return values.map((value) => ({ value: pad(value), label: pad(value) }));
}

/**
 * Rounds to the nearest 15-minute step. If that lands in the past (e.g. 12:50 → 12:45),
 * the next step in the future is used instead so the picker opens on a confirmable time.
 */
export function roundToPickerStep(date: Date): Date {
    const nearest = roundToNearestMinutes(date, { nearestTo: minuteStep });
    if (!isPast(nearest)) return nearest;
    return roundToNearestMinutes(addMinutes(date, 1), {
        nearestTo: minuteStep,
        roundingMethod: "ceil",
    });
}

function LocalDateTimePicker(props: LocalDateTimePickerProps) {
    const { onChange, onConfirm, onCancel, showTime, initialDate } = props;
    const withTime = showTime ?? true;

    const anchorRef = useRef<HTMLDivElement>(null);
    const [date, setDate] = useState<Date>(() =>
        roundToPickerStep(initialDate ?? new Date()),
    );

    const updateDate = (newDate: Date) => {
        setDate(newDate);
        onChange?.(newDate);
    };

    const isInPast = withTime && isPast(date);

    return (
        <>
            <div ref={anchorRef} />
            <Popover
                open
                onOpenChange={(open) => {
                    if (!open) onCancel();
                }}
            >
                <PopoverContent
                    anchor={anchorRef}
                    side="bottom"
                    align="start"
                    className="w-auto gap-0 p-0"
                    positionerClassName="z-[var(--stack-order-level5)]"
                >
                    <Calendar
                        mode="single"
                        required
                        selected={date}
                        defaultMonth={date}
                        weekStartsOn={1}
                        disabled={{ before: startOfToday() }}
                        onSelect={(day) => {
                            updateDate(
                                set(day, {
                                    hours: getHours(date),
                                    minutes: getMinutes(date),
                                    seconds: 0,
                                    milliseconds: 0,
                                }),
                            );
                        }}
                    />
                    {withTime && (
                        <Field orientation="horizontal" className="border-t px-3 py-2">
                            <FieldLabel htmlFor="local-date-time-picker-hour">
                                Time
                            </FieldLabel>
                            <div className="flex items-center gap-1">
                                <Select
                                    items={toItems(hours)}
                                    value={pad(getHours(date))}
                                    onValueChange={(value) => {
                                        if (typeof value !== "string") return;
                                        updateDate(set(date, { hours: Number(value) }));
                                    }}
                                >
                                    <SelectTrigger
                                        id="local-date-time-picker-hour"
                                        aria-label="Hour"
                                        aria-invalid={isInPast}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="min-w-0">
                                        {hours.map((hour) => (
                                            <SelectItem
                                                key={hour}
                                                value={pad(hour)}
                                                disabled={isPast(
                                                    endOfHour(
                                                        set(date, { hours: hour }),
                                                    ),
                                                )}
                                            >
                                                {pad(hour)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span aria-hidden>:</span>
                                <Select
                                    items={toItems(stepMinutes)}
                                    value={pad(getMinutes(date))}
                                    onValueChange={(value) => {
                                        if (typeof value !== "string") return;
                                        updateDate(
                                            set(date, {
                                                minutes: Number(value),
                                                seconds: 0,
                                                milliseconds: 0,
                                            }),
                                        );
                                    }}
                                >
                                    <SelectTrigger
                                        aria-label="Minute"
                                        aria-invalid={isInPast}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="min-w-0">
                                        {stepMinutes.map((minute) => (
                                            <SelectItem
                                                key={minute}
                                                value={pad(minute)}
                                                disabled={isPast(
                                                    set(date, {
                                                        minutes: minute,
                                                        seconds: 59,
                                                        milliseconds: 999,
                                                    }),
                                                )}
                                            >
                                                {pad(minute)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </Field>
                    )}
                    <div className="flex justify-between gap-2 border-t p-3">
                        <Button variant="ghost" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            disabled={isInPast}
                            disabledReason="Pick a time in the future"
                            onClick={() => {
                                if (withTime && isPast(date)) return;
                                onConfirm(date);
                            }}
                        >
                            Confirm
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>
        </>
    );
}

export default LocalDateTimePicker;
